import React, { useState, useEffect } from "react";
import LoadingBtn from "../../components/LoadingBtn";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import endpoints from "../../api/endpoints";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import Image from "../../images/user-avatar-80.png";
import { toast } from "react-toastify";

function AccountPanel(props) {
  const { t } = useTranslation();
  const [avatar, setAvatar] = useState(Image);
  const [newAvatar, setNewAvatar] = useState();
  const [newAvatarBlob, setNewAvatarBlob] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  // Upload Profile Picture

  const uploadProfilePictureHandler = async () => {
    if (newAvatar) {
      try {
        setIsUploading(true);

        // Upload Profile Picture
        let formData = new FormData();
        formData.append("avatar", newAvatarBlob);

        const res = await axiosPrivate.post(endpoints.UPLOAD_AVATAR, formData);

        setAvatar(res.data.url);

        // Reset
        setNewAvatarBlob(null);
        setNewAvatar(null);

        // Set New Profile Picture
        await axiosPrivate.patch(endpoints.PROFILE_URL, {
          profilePic: res.data.url,
        });

        toast.success(t("Profile Pic Updated"));
      } catch (error) {
        console.error(error);
        // navigate('/signin', { state: { from: location }, replace: true });
      } finally {
        setIsUploading(false);
        props.updateHeader();
      }
    }
  };

  const updateUserProfile = async (values) => {
    // Send patch request
    try {
      setIsSaving(true);
      await axiosPrivate.patch(endpoints.PROFILE_URL, values);

      toast.success(t("Profile Updated"));
    } catch (e) {
      console.log(e.response.data.error);
      toast.error(e.response.data.error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Save changes
    uploadProfilePictureHandler();
  }, [newAvatar]);

  const profileFormSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, t("Too Short"))
      .max(50, t("Too Long"))
      .required(t("Required")),
    email: Yup.string()
      .email(t("Invalid email address"))
      .required(t("Required")),
  });

  const profileForm = useFormik({
    initialValues: {
      firstName: props?.user?.firstName,
      email: props?.user?.email,
    },
    validationSchema: profileFormSchema,
    onSubmit: (values) => {
      updateUserProfile(values);
    },
  });

  useEffect(() => {
    // Update avatar
    if (props?.user?.profilePic) {
      setAvatar(props?.user?.profilePic);
    }
  }, []);

  return (
    <div className="border-gray-200 shadow p-4 rounded-lg">
      <form onSubmit={profileForm.handleSubmit}>
        {/* Panel body */}
        <div className="space-y-6">
          <h2 className="text-2xl text-neutral-800 dark:text-white font-bold mb-5">
            {t("My Account")}
          </h2>
          {/* Picture */}
          <section>
            <div className="flex items-center">
              <div className="group relative mr-4">
                <div className="relative flex items-center justify-center">
                  {isUploading ? (
                    <svg
                      className="z-10 h-1/2 w-1/2 absolute animate-spin fill-current shrink-0"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                  ) : null}

                  <img
                    className={`w-32 h-32 object-cover transition duration-200 ease-in-out rounded-full  group-hover:blur-sm ${
                      isUploading ? "opacity-50 blur-sm" : ""
                    }`}
                    src={newAvatar ? newAvatar : avatar}
                    width="80"
                    height="80"
                    alt="User upload"
                  />
                  <div className="h-full w-full absolute flex items-center justify-center">
                    {isUploading ? (
                      <span
                        htmlFor="avatar-upload"
                        className="rounded-full px-2 btn-sm shadow-none text-transparent group-hover:visible group-hover:bg-black group-hover:text-white transition-all ease-in-out duration-200"
                      >
                        <svg
                          className="animate-spin w-1 h-1 fill-current shrink-0"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                        </svg>
                        <span className="ml-2">{t("Saving")}</span>
                      </span>
                    ) : (
                      <label
                        htmlFor="avatar-upload"
                        className="cursor-pointer group-hover:scale-105 rounded-full px-4 btn-sm shadow-none text-transparent group-hover:visible group-hover:bg-black group-hover:text-white transition-all ease-in-out duration-200"
                      >
                        {t("Edit")}
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-x-2">
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const fileReader = new FileReader();
                    fileReader.onload = () => {
                      if (fileReader.readyState === 2) {
                        setNewAvatar(fileReader.result);
                        setNewAvatarBlob(e.target.files[0]);
                      }
                    };
                    fileReader.readAsDataURL(e.target.files[0]);
                  }}
                />
              </div>
            </div>
          </section>
          {/* Business Profile */}
          <section>
            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="firstName"
                >
                  {t("First Name")}
                </label>
                <input
                  id="firstName"
                  className="form-input w-full dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                  type="text"
                  onChange={profileForm.handleChange}
                  value={profileForm.values.firstName}
                />
              </div>
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  {t("Email")}
                </label>
                <input
                  id="email"
                  className="form-input w-full dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                  type="text"
                  onChange={profileForm.handleChange}
                  value={profileForm.values.email}
                />
              </div>
            </div>
          </section>
        </div>
        {/* Panel footer */}
        <footer>
          <div className="flex flex-col py-5 border-neutral-200">
            <div className="flex">
              {isSaving ? (
                <LoadingBtn text={t("Saving")} />
              ) : (
                <button
                  type="submit"
                  className="px-10 btn bg-sky-500 hover:bg-sky-600 text-white"
                >
                  {t("Save")}
                </button>
              )}
            </div>
          </div>
        </footer>
      </form>
    </div>
  );
}

export default AccountPanel;
